import { useMemo } from 'react';

import { VisitHistoryMedicalLeave, VisitHistoryMedicalLeaveQuery } from '@dreryk/egabinet-visit-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { booleanFormatter, dateFormatter, integerFormatter, Translate } from '@dreryk/react-common';
import {
   AlignItems,
   CheckIcon,
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   IconFillColor,
   IconSize,
   IconWrapper,
   JustifyContent,
   Table,
   TableColumnConfiguration,
   Text,
   TextSeparator,
   TextSeparatorType,
   ValueRendererProps,
} from '@dreryk/react-components';

import { visitHistoryTranslationKeys } from '../translations';

const useVisitHistoryMedicalLeaveDataConfiguration = (
   visitId: number
): LazyLoadedDataConfigurationByCqrsQuery<VisitHistoryMedicalLeave> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<VisitHistoryMedicalLeave>(() => {
      const query = new VisitHistoryMedicalLeaveQuery();
      query.visitId = visitId;
      return query;
   });
};

const useVisitHistoryMedicalLeaveColumnsConfiguration =
   // eslint-disable-next-line max-lines-per-function
   (): TableColumnConfiguration<VisitHistoryMedicalLeave>[] => {
      // eslint-disable-next-line max-lines-per-function
      return useMemo<TableColumnConfiguration<VisitHistoryMedicalLeave>[]>(() => {
         const columnsWidth = createDynamicColumnsWidth({
            period: '178px',
            daysCount: '52px',
            serialNumber: '112px',
            forGuardian: '84px',
            payer: '100%',
            payerPueAccount: '48px',
         });

         return [
            {
               name: 'period',
               label: visitHistoryTranslationKeys.medicalLeavesTable.period,
               width: columnsWidth.period,
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,
               accessor: (medicalLeave) => medicalLeave,

               renderer: ({
                  value: { dateFrom, dateTo },
               }: ValueRendererProps<VisitHistoryMedicalLeave, VisitHistoryMedicalLeave>): JSX.Element => {
                  const formattedDateFrom = dateFrom ? dateFormatter(dateFrom) : null;
                  const formattedDateTo = dateTo ? dateFormatter(dateTo) : null;

                  return (
                     <TextSeparator separator={TextSeparatorType.SPACE_HYPHEN_SPACE}>
                        <Text testId={'dateFrom'}>{formattedDateFrom}</Text>
                        <Text testId={'dateTo'}>{formattedDateTo}</Text>
                     </TextSeparator>
                  );
               },
            },
            {
               name: 'daysCount',
               label: visitHistoryTranslationKeys.medicalLeavesTable.daysCount,
               width: columnsWidth.daysCount,
               sortingDisabled: true,
               justifyContent: JustifyContent.END,
               alignItems: AlignItems.CENTER,
               accessor: 'daysCount',
               formatter: integerFormatter,
            },
            {
               name: 'serialNumber',
               label: visitHistoryTranslationKeys.medicalLeavesTable.serialNumber,
               width: columnsWidth.serialNumber,
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,
               accessor: 'serialNumber',
            },
            {
               name: 'forGuardian',
               label: visitHistoryTranslationKeys.medicalLeavesTable.forGuardian,
               width: columnsWidth.forGuardian,
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,
               accessor: 'forGuardian',
               formatter: booleanFormatter,
            },
            {
               name: 'payer',
               label: visitHistoryTranslationKeys.medicalLeavesTable.payer,
               width: columnsWidth.payer,
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,
               accessor: (medicalLeave) => medicalLeave,

               renderer: ({
                  value: { identificationNumberType, identificationNumber, insuranceType },
               }: ValueRendererProps<VisitHistoryMedicalLeave, VisitHistoryMedicalLeave>): JSX.Element => {
                  if (identificationNumber && identificationNumberType) {
                     return (
                        <TextSeparator separator={TextSeparatorType.COLON_SPACE}>
                           <Text testId={'type'}>
                              <Translate>
                                 {
                                    visitHistoryTranslationKeys.medicalLeavesTable.identificationNumberType[
                                       identificationNumberType
                                    ]
                                 }
                              </Translate>
                           </Text>
                           <Text testId={'number'}>{identificationNumber}</Text>
                        </TextSeparator>
                     );
                  }

                  return (
                     <Text testId={'insuranceType'}>
                        <Translate>
                           {visitHistoryTranslationKeys.medicalLeavesTable.insuranceType[insuranceType ?? 'NONE']}
                        </Translate>
                     </Text>
                  );
               },
            },
            {
               name: 'payerPueAccount',
               label: visitHistoryTranslationKeys.medicalLeavesTable.payerPueAccount,
               width: columnsWidth.payerPueAccount,
               sortingDisabled: true,
               justifyContent: JustifyContent.CENTER,
               alignItems: AlignItems.CENTER,
               accessor: (medicalLeave) => medicalLeave.payerPueAccount,

               renderer: ({
                  value: payerPueAccount,
               }: ValueRendererProps<VisitHistoryMedicalLeave, boolean>): JSX.Element | null =>
                  payerPueAccount ? (
                     <IconWrapper size={IconSize.SMALL} testId={'payerPueAccount'} fillColor={IconFillColor.PRIMARY}>
                        <CheckIcon />
                     </IconWrapper>
                  ) : null,
            },
         ];
      }, []);
   };

type VisitHistoryMedicalLeavesTableProps = {
   readonly visitId: number;
};

export const VisitHistoryMedicalLeavesTable = (props: VisitHistoryMedicalLeavesTableProps): JSX.Element => {
   return (
      <Table
         testId={'medicalLeaves'}
         dataConfiguration={useVisitHistoryMedicalLeaveDataConfiguration(props.visitId)}
         columns={useVisitHistoryMedicalLeaveColumnsConfiguration()}
         keyExtractor={'rowId'}
         pageSize={1000}
         placeholder={
            <EmptyStatePlaceholder
               testId={'noMedicalLeaves'}
               heading={visitHistoryTranslationKeys.medicalLeavesTable.noMedicalLeaves}
               subheading={visitHistoryTranslationKeys.medicalLeavesTable.noMedicalLeavesWereIssued}
            />
         }
      />
   );
};
